import { ArrowSquareOutIcon, CaretRightIcon, DownloadSimpleIcon, EyeIcon, XIcon } from '@phosphor-icons/react'
import { mappedColors, typography } from '@pixee/design-system'
import * as Collapsible from '@radix-ui/react-collapsible'
import * as Dialog from '@radix-ui/react-dialog'
import * as Tabs from '@radix-ui/react-tabs'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { useQueryClient } from '@tanstack/react-query'
import { type ColumnDef } from '@tanstack/react-table'
import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { match, P } from 'ts-pattern'
import {
  usePolarisPushPreflight,
  type PolarisPushConfirmationModalProps,
} from '../../hooks/use-polaris-push-preflight.ts'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import {
  getScanFindingQueryKeyPrefix,
  getScanFindingResultsQueryKeyPrefix,
  getScanFindingsQueryKeyPrefix,
  invalidateGetPolarisStatuses,
  useGetAnalysisPreferences,
  useGetPushesByIds,
  useGetResultActivitySSE,
  useGetScan,
  useGetScanAnalysis,
  useGetScanFinding,
  useGetScanFindingResults,
  useGetTriageResultArticle,
  useGetTriageResultReport,
  useGetTriageResultVerification,
  usePostPush,
} from '../../utils/api-client/api-hooks.ts'
import {
  type CompletedTriageOutcome,
  type FindingResponseWithoutEmbed,
  type FixResultResponse,
  type PushResponse,
  type ResultsTriageResultResponse,
  type ResultsVerificationResponse,
} from '../../utils/api-client/api-schemas.ts'
import { shouldShowTriageConfetti } from '../../utils/confirmation-utils.ts'
import {
  getAnalysisMetadata,
  getFirstCompletedTriageResultFromResults,
  getResultById,
  inferLanguageFromFilename,
  reformatFindingTriageArticleMarkdown,
  reformatFindingTriageReportMarkdown,
  wrapInCodeFence,
} from '../../utils/finding-utils.ts'
import { getIntegrationName } from '../../utils/repository-utils.tsx'
import { joinCopySections } from '../../utils/text-utils.ts'
import { AnalysisActivityTab, type AnalysisActivityTabProps } from '../activity-feed/analysis-activity-tab.tsx'
import { AnalysisTransitionOverlay } from '../activity-feed/analysis-transition-overlay.tsx'
import { getOutcomeBannerCopyText, OutcomeBanner, type OutcomeBannerOutcome } from '../activity-feed/outcome-banner.tsx'
import { StatusNotification, type StatusNotificationStatus } from '../activity-feed/status-notification.tsx'
import { useActivityResultSelection } from '../activity-feed/use-activity-result-selection.ts'
import { useActivityTabBehavior } from '../activity-feed/use-activity-tab-behavior.ts'
import {
  convertVerificationResultToLabel,
  VerificationOutcomeBanner,
} from '../activity-feed/verification-outcome-banner.tsx'
import { CopyButton } from '../buttons/copy-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { ProgressButton } from '../buttons/progress-button.tsx'
import { DrawerConfetti } from '../drawer-confetti.tsx'
import { ErrorBoundary } from '../error-boundaries/error-boundary'
import { PolarisPushConfirmationPrompt } from '../modals/polaris-push-confirmation-modal.tsx'
import { OutcomeReasonDetails } from '../outcomes/outcome-reason-details.tsx'
import { isBlockedOrExcludedTriageOutcome } from '../outcomes/outcome-utils.ts'
import { RenderedMarkdown } from '../rendered-markdown.tsx'
import * as scrollableTabBarStyles from '../scrollable-tab-bar/scrollable-tab-bar.css.ts'
import { ScrollableTabBar } from '../scrollable-tab-bar/scrollable-tab-bar.tsx'
import { Spinner } from '../spinner.tsx'
import { Table, type RowDataWithId } from '../table/table.tsx'
import { AnalyzerStrategyTag } from '../tags/analyzer-strategy-tag.tsx'
import { BetaTag } from '../tags/beta-tag'
import { TooltipWithHover } from '../tooltip.tsx'
import { AnalysisPreferencesTab } from './analysis-preferences-tab.tsx'
import { resolveVisibleTab, useDrawerContentDimensions } from './drawer-utils.ts'
import { FeedbackBar } from './feedback-bar.tsx'
import * as footerStyles from './floating-footer.css'
import { ResultNotFoundFallback } from './result-not-found-fallback.tsx'
import * as styles from './triage-finding-drawer.css.ts'
import { useNavigateScanFindingDrawers } from './use-navigate-scan-finding-drawers.ts'

type TriageArticleProps = Pick<ReturnType<typeof useGetTriageResultArticle>, 'data' | 'isLoading' | 'isError'>

type TriageReportProps = Pick<ReturnType<typeof useGetTriageResultReport>, 'data' | 'isLoading' | 'isError'>

type TriageVerificationProps = Pick<ReturnType<typeof useGetTriageResultVerification>, 'data' | 'isLoading' | 'isError'>

// #region Wrapper

export type TriageFindingDrawerProps = {
  scanId: string
  memory?: string
}

export const TriageFindingDrawer: React.FC<TriageFindingDrawerProps> = ({ scanId, memory }) => {
  const { findingTriageFindingId, resultId, onClose, onOpenFindingFixDrawer, onNavigateToResult, isSca } =
    useNavigateScanFindingDrawers({
      scanId,
    })

  const isOpen = !!findingTriageFindingId && !isSca
  const { confirmationDialogProps, ...props } = useTriageFindingDrawerBase({
    scanId: isOpen ? scanId : undefined,
    findingId: isOpen ? findingTriageFindingId : undefined,
    resultId: isOpen ? resultId : undefined,
    onClose,
    onOpenFindingFixDrawer,
    onNavigateToResult,
  })

  if (!isOpen) return null

  return (
    <>
      <TriageFindingDrawerPure {...props} scanId={scanId} {...(memory !== undefined ? { memory } : {})} />
      <PolarisPushConfirmationPrompt dialogProps={confirmationDialogProps} />
    </>
  )
}

// #endregion

// #region Hook

type UseTriageFindingDrawerBaseOptions = {
  scanId?: string
  findingId?: string
  resultId?: string
  onClose: TriageFindingDrawerPureProps['onClose']
  onOpenFindingFixDrawer: TriageFindingDrawerPureProps['onOpenFindingFixDrawer']
  onNavigateToResult?: (resultId: string) => unknown
}

export function useTriageFindingDrawerBase({
  scanId,
  findingId,
  resultId,
  onClose,
  onOpenFindingFixDrawer,
  onNavigateToResult,
}: UseTriageFindingDrawerBaseOptions) {
  const {
    data: finding,
    isLoading: isFindingLoading,
    isError: isFindingError,
    isSuccess: isFindingSuccess,
  } = useGetScanFinding({
    scanId,
    findingId,
  })

  const { data: results } = useGetScanFindingResults({
    scanId,
    findingId,
    representative: !resultId,
    refetchInterval: findingId ? 1000 : undefined,
  })

  // Representative results exclude in-progress results, so fetch all results
  // separately for live detection during re-analysis.
  const { data: allResults } = useGetScanFindingResults({
    scanId,
    findingId,
    representative: false,
    enabled: !resultId,
    refetchInterval: findingId ? 1000 : undefined,
  })

  const triageResult = resultId
    ? getResultById({ results, resultId, type: 'triage' })
    : getFirstCompletedTriageResultFromResults(results)

  // Only link the fix from the same analysis as the triage result. The
  // representative-results endpoint selects the representative of each type
  // independently, so the representative triage and fix can originate from
  // different analyses; surfacing such a fix would link an unrelated fix
  // (ISS-6418). When the triage's own analysis produced no fix, show none.
  const fixResult = triageResult
    ? results?._embedded?.items.find(
        (r): r is FixResultResponse => r.type === 'fix' && r._links.analysis.href === triageResult._links.analysis.href
      )
    : undefined

  const isFixInProgress = !!fixResult && !fixResult.outcome

  const isResultNotFound = !!resultId && isFindingSuccess && !triageResult

  // Only fetch article/report/verification when the result has an outcome that actually has content.
  // In-progress results don't have content yet, and blocked/excluded outcomes have none (the
  // endpoints 404) — the Summary tab renders their reason/details and the Full Report tab is hidden.
  const outcomeResultId =
    triageResult?.outcome && !isBlockedOrExcludedTriageOutcome(triageResult.outcome) ? triageResult.id : undefined
  const triageArticleQuery = useGetTriageResultArticle({ resultId: outcomeResultId })
  const triageReportQuery = useGetTriageResultReport({ resultId: outcomeResultId })
  const triageVerificationQuery = useGetTriageResultVerification({ resultId: outcomeResultId })

  // Use all results for activity selection (falls back to representative while loading)
  const activityResults = resultId ? results : (allResults ?? results)
  const { activeResultId, isLive: hasInProgressResult } = useActivityResultSelection({
    results: activityResults,
    findingType: 'triage',
  })

  // isLive reflects the VIEWED result, not any result
  const isLive = !!triageResult && !triageResult.outcome

  const hasProcessedInitialResultsRef = useRef(false)
  const openedWithLiveAnalysisRef = useRef(false)
  // Wait specifically for the all-results query (no fallback) to avoid
  // capturing stale isLive=false from cached representative results.
  const liveDetectionResults = resultId ? results : allResults
  // Reset when results become unavailable (drawer closed or finding changed),
  // so the next open captures fresh initial state instead of stale values.
  if (liveDetectionResults === undefined) {
    hasProcessedInitialResultsRef.current = false
    openedWithLiveAnalysisRef.current = false
  }
  // Capture whether analysis was already live when results first loaded.
  // This distinguishes "drawer opened during analysis" (redirect to in-progress result)
  // from "analysis started while viewing drawer" (show notification instead).
  if (!hasProcessedInitialResultsRef.current && liveDetectionResults !== undefined) {
    hasProcessedInitialResultsRef.current = true
    openedWithLiveAnalysisRef.current = hasInProgressResult
  }

  // When the drawer opens with an in-progress analysis, redirect to the
  // in-progress result URL so it renders naturally (activity-only view,
  // then summary when complete, with correct fix association).
  //
  // Depend on all-results having loaded: `openedWithLiveAnalysisRef` is captured during the
  // render where all-results first loads, which need not change `activeResultId` (the
  // representative query can supply it first). Without this dep the effect runs once while
  // the ref is still false and never re-runs, leaving the drawer stuck in the tabbed view.
  const hasLoadedLiveDetectionResults = liveDetectionResults !== undefined
  useEffect(() => {
    if (openedWithLiveAnalysisRef.current && !resultId && activeResultId) {
      onNavigateToResult?.(activeResultId)
    }
  }, [resultId, activeResultId, onNavigateToResult, hasLoadedLiveDetectionResults])

  const queryClient = useQueryClient()
  const sseResultId = openedWithLiveAnalysisRef.current && !resultId ? activeResultId : triageResult?.id
  const activityStream = useGetResultActivitySSE(sseResultId, 'triage')

  const analysisHref = triageResult?._links.analysis.href
  const analysisId = analysisHref?.split('/').pop()
  const { data: analysis } = useGetScanAnalysis({ analysisId })

  const analysisMetadata = useMemo(
    () => getAnalysisMetadata({ analysis, resultCompleted: triageResult?.completed }),
    [analysis, triageResult?.completed]
  )

  useEffect(() => {
    if (activityStream.connectionStatus === 'finished' && analysisId) {
      queryClient.invalidateQueries({ queryKey: ['scan-analysis', analysisId] })
    }
  }, [activityStream.connectionStatus, analysisId, queryClient])

  const activityProps: AnalysisActivityTabProps | undefined = activityStream.isEndpointAvailable
    ? {
        events: activityStream.events,
        connectionStatus: activityStream.connectionStatus,
        error: activityStream.error,
        metadata: analysisMetadata,
        isLive,
      }
    : undefined

  const onViewActiveResult = activeResultId ? () => onNavigateToResult?.(activeResultId) : undefined

  const isRepresentative = !resultId

  const { data: preferencesData } = useGetAnalysisPreferences({ analysisId: analysis?.id })

  const { onPush, isPushInProgress, confirmationDialogProps, pushIntegrationName, currentPushState } =
    usePushClassification({
      scanId,
      triageResult,
      finding,
    })

  return {
    finding,
    triageResult,
    fixResult,
    isFixInProgress,
    isRepresentative,
    isFindingLoading,
    isFindingError,
    isResultNotFound,
    triageArticleProps: {
      data: triageArticleQuery.data,
      isLoading: triageArticleQuery.isLoading,
      isError: triageArticleQuery.isError,
    },
    triageReportProps: {
      data: triageReportQuery.data,
      isLoading: triageReportQuery.isLoading,
      isError: triageReportQuery.isError,
    },
    triageVerificationProps: {
      data: triageVerificationQuery.data,
      isLoading: triageVerificationQuery.isLoading,
      isError: triageVerificationQuery.isError,
    },
    activityProps,
    isLive,
    hasInProgressResult,
    onViewActiveResult,
    onClose,
    onOpenFindingFixDrawer,
    onPush,
    isPushInProgress,
    confirmationDialogProps,
    pushIntegrationName,
    currentPushState,
    memory: preferencesData ?? undefined,
  } satisfies Omit<
    TriageFindingDrawerPureProps,
    'defaultTab' | '_storyShowConfetti' | '_storyNotification' | '_storyShowConfirmation'
  > & {
    // Confirmation data, not Pure props: the impure wrapper renders the dialog, so Pure stays
    // drivable from plain data in tests and stories.
    confirmationDialogProps?: PolarisPushConfirmationModalProps
  }
}

// Owns the push-a-classification-to-the-scanner flow: eligibility, the queueing mutation, and the
// poll that waits for every queued push to reach a terminal state before toasting the outcome.
const usePushClassification = ({
  scanId,
  triageResult,
  finding,
}: {
  scanId: string | undefined
  triageResult: ResultsTriageResultResponse | undefined
  finding: FindingResponseWithoutEmbed | undefined
}) => {
  const queryClient = useQueryClient()

  const { data: scan } = useGetScan({ id: scanId })
  const scanIntegration = scan?._embedded?.integration
  const isPushEligible = scanIntegration?.capabilities?.includes('triage-push') === true
  const pushableTriageResultId =
    isPushEligible &&
    triageResult?.outcome?.type === 'completed-triage' &&
    triageResult.outcome.suggested_status !== null &&
    triageResult.outcome.suggested_status !== undefined
      ? triageResult.id
      : undefined

  const { addToast } = useToastService()
  const [trackedPushIds, setTrackedPushIds] = useState<string[] | null>(null)
  const showPushError = useShowMutationError<typeof usePostPush>({
    prefix: () => 'Error pushing classification',
  })
  const pushMutation = usePostPush({
    onSuccess: data => {
      if (data.pushes.length > 0) {
        addToast({ message: <>Queued classification for push</>, variant: 'info' })
        setTrackedPushIds(data.pushes.map(push => push.id))
        if (scanId) {
          queryClient.invalidateQueries({ queryKey: getScanFindingsQueryKeyPrefix(scanId) })
          queryClient.invalidateQueries({ queryKey: getScanFindingQueryKeyPrefix(scanId) })
          queryClient.invalidateQueries({ queryKey: getScanFindingResultsQueryKeyPrefix(scanId) })
        }
      } else {
        addToast({
          message: <>Classification was skipped ({data.skipped[0]?.reason ?? 'ineligible'})</>,
          variant: 'warning',
        })
      }
    },
    onError: showPushError,
  })
  // Only Polaris carries remote triage state a push could overwrite, so the preflight is gated on the
  // integration's type rather than its display label.
  const { preflight, confirmationDialogProps, isPushPending } = usePolarisPushPreflight({
    integrationType: scanIntegration?.type,
    push: pushMutation.mutateAsync,
  })
  const { data: pushesData } = useGetPushesByIds({
    ids: trackedPushIds ?? [],
    enabled: trackedPushIds !== null,
  })
  useEffect(() => {
    if (trackedPushIds === null || pushesData === undefined) {
      return
    }
    const allTerminal =
      pushesData.pushes.length > 0 &&
      pushesData.pushes.every(push => push.status === 'SUCCESS' || push.status === 'FAILED')
    if (!allTerminal) {
      return
    }
    const hasFailure = pushesData.pushes.some(push => push.status === 'FAILED')
    const firstFailureReason = pushesData.pushes.find(push => push.status === 'FAILED')?.error_reason
    addToast({
      message: hasFailure ? (
        <>
          Error pushing classification
          {firstFailureReason ? `: ${firstFailureReason}` : ''}
        </>
      ) : (
        <>Classification successfully pushed</>
      ),
      variant: hasFailure ? 'error' : 'success',
    })
    setTrackedPushIds(null)
    invalidateGetPolarisStatuses(queryClient)
    if (scanId) {
      queryClient.invalidateQueries({ queryKey: getScanFindingsQueryKeyPrefix(scanId) })
      queryClient.invalidateQueries({ queryKey: getScanFindingQueryKeyPrefix(scanId) })
      queryClient.invalidateQueries({ queryKey: getScanFindingResultsQueryKeyPrefix(scanId) })
    }
  }, [trackedPushIds, pushesData, addToast, queryClient, scanId])

  const onPush = useCallback(() => {
    if (!pushableTriageResultId) {
      return
    }
    return preflight({ triageResultIds: [pushableTriageResultId] })
  }, [preflight, pushableTriageResultId])

  return {
    onPush: pushableTriageResultId ? onPush : undefined,
    isPushInProgress: isPushPending,
    confirmationDialogProps,
    pushIntegrationName: getIntegrationName(scanIntegration),
    currentPushState:
      triageResult?.latest_push_state ??
      (triageResult && finding?.push_state?.triage_result_ids.includes(triageResult.id)
        ? finding.push_state
        : undefined),
  }
}

// #endregion

// #region Pure Component

type TriageFindingDrawerTab = 'summary' | 'full-report' | 'verification' | 'analysis-activity' | 'preferences'

export type TriageFindingDrawerPureProps = {
  defaultTab?: TriageFindingDrawerTab
  /** The scan the finding was read from; lets the feedback bar scope feedback to the repository's project. */
  scanId?: string
  finding?: FindingResponseWithoutEmbed
  triageResult?: ResultsTriageResultResponse
  fixResult?: FixResultResponse
  isFixInProgress: boolean
  isRepresentative: boolean
  isFindingLoading: boolean
  isFindingError: boolean
  isResultNotFound?: boolean
  triageArticleProps: TriageArticleProps
  triageReportProps: TriageReportProps
  triageVerificationProps: TriageVerificationProps
  activityProps?: AnalysisActivityTabProps
  isLive?: boolean
  hasInProgressResult?: boolean
  onViewActiveResult?: () => unknown
  onClose: () => unknown
  onOpenFindingFixDrawer: (findingId: string | undefined, resultId?: string) => unknown
  onPush?: () => unknown
  isPushInProgress?: boolean
  pushIntegrationName?: string
  currentPushState?: PushResponse
  memory?: string
  _storyShowConfetti?: boolean
  _storyNotification?: { status: StatusNotificationStatus; text: string; actionLabel?: string }
  _storyShowConfirmation?: boolean
}

export const TriageFindingDrawerPure: React.FC<TriageFindingDrawerPureProps> = ({
  defaultTab = 'summary',
  scanId,
  finding,
  triageResult,
  fixResult,
  isFixInProgress,
  isFindingLoading,
  isFindingError,
  isResultNotFound,
  triageArticleProps,
  triageReportProps,
  triageVerificationProps,
  activityProps,
  isLive,
  hasInProgressResult,
  onViewActiveResult,
  onClose,
  onOpenFindingFixDrawer,
  onPush,
  isPushInProgress,
  pushIntegrationName,
  currentPushState,
  memory,
  _storyShowConfetti,
  _storyNotification,
  _storyShowConfirmation,
}) => {
  const { drawerContentRef, contentDimensions } = useDrawerContentDimensions({ isEnabled: !!finding })
  const [storyConfetti, setStoryConfetti] = useState(false)

  const completedTriageOutcome = triageResult?.outcome?.type === 'completed-triage' ? triageResult.outcome : undefined
  const triageOutcome: OutcomeBannerOutcome | undefined =
    triageResult?.outcome && triageResult.outcome.type !== 'cancelled-triage' ? triageResult.outcome : undefined

  const { summaryCopyText, fullReportCopyText } = useTriageTabCopyTexts({
    triageOutcome,
    articleMarkdown: triageArticleProps.data,
    reportMarkdown: triageReportProps.data,
  })

  const isBlockedOrExcluded = isBlockedOrExcludedTriageOutcome(triageResult?.outcome)

  const isActivityTabVisible = !!activityProps && !isBlockedOrExcluded

  // Blocked/excluded findings have no triage report, so hide the Full Report tab (its content would
  // only ever fail to load); the reason/details live on the Summary tab instead.
  const isFullReportTabVisible = !isBlockedOrExcluded

  const {
    selectedTab: hookSelectedTab,
    handleTabChange,
    notification: hookNotification,
    switchToActivityTab,
    showConfirmation: hookShowConfirmation,
    showConfetti,
    onConfettiComplete,
  } = useActivityTabBehavior<TriageFindingDrawerTab>({
    isLive: isLive ?? false,
    connectionStatus: activityProps?.connectionStatus,
    defaultTab,
    activityTab: 'analysis-activity',
    isActivityTabVisible,
    shouldShowConfetti: _storyShowConfetti === undefined ? shouldShowTriageConfetti(completedTriageOutcome) : false,
  })

  useEffect(() => {
    if (isLive) {
      switchToActivityTab()
    }
  }, [isLive, switchToActivityTab])

  const inProgressNotification =
    hasInProgressResult && !isLive ? { status: 'in-progress' as const, text: 'Analysis in progress' } : undefined
  const notification = _storyNotification ?? inProgressNotification ?? hookNotification
  const notificationActionLabel = _storyNotification !== undefined ? _storyNotification.actionLabel : 'View'
  const notificationAction = notificationActionLabel
    ? () => {
        switchToActivityTab()
        onViewActiveResult?.()
      }
    : undefined
  const showConfirmation = _storyShowConfirmation ?? hookShowConfirmation
  const rawSelectedTab = _storyShowConfirmation ? ('analysis-activity' as TriageFindingDrawerTab) : hookSelectedTab
  // The tab selection persists across in-place navigation to a different finding, so the previously
  // selected tab can be one that isn't shown for the new finding (e.g. arriving at a blocked/excluded
  // finding while Full Report, Verification, or Analysis Activity was selected). Fall back to Summary
  // whenever the selected tab isn't currently visible — mirroring each tab's trigger condition — so
  // the drawer never renders an orphaned, hidden tab's content (Summary is always visible).
  const isVerificationTabVisible =
    !triageVerificationProps.isLoading && (Boolean(triageVerificationProps.data) || triageVerificationProps.isError)
  const isPreferencesTabVisible = Boolean(memory)
  // Paired with the same booleans that gate the Tabs.Triggers (passed to TriageResultTabs), so trigger
  // visibility and the fallback below share one source of truth. Summary is always present.
  const tabVisibility: Record<TriageFindingDrawerTab, boolean> = {
    summary: true,
    'full-report': isFullReportTabVisible,
    verification: isVerificationTabVisible,
    'analysis-activity': isActivityTabVisible,
    preferences: isPreferencesTabVisible,
  }
  const visibleTabs = (Object.keys(tabVisibility) as TriageFindingDrawerTab[]).filter(tab => tabVisibility[tab])
  const selectedTab = resolveVisibleTab({ selectedTab: rawSelectedTab, visibleTabs, fallbackTab: 'summary' })
  const showActivityOnlyView = !!isLive || showConfirmation

  const AUTO_NAVIGATE_TO_FIX_DELAY_MS = 2000
  const [showFixTransitionOverlay, setShowFixTransitionOverlay] = useState(false)
  const prevShowConfirmationRef = useRef(showConfirmation)
  useEffect(() => {
    const wasConfirming = prevShowConfirmationRef.current
    prevShowConfirmationRef.current = showConfirmation

    if (wasConfirming && !showConfirmation && isFixInProgress && fixResult && finding) {
      const showOverlayTimer = setTimeout(() => {
        setShowFixTransitionOverlay(true)
      }, AUTO_NAVIGATE_TO_FIX_DELAY_MS)
      const navigateTimer = setTimeout(() => {
        setShowFixTransitionOverlay(false)
        onOpenFindingFixDrawer(finding.id, fixResult.id)
      }, AUTO_NAVIGATE_TO_FIX_DELAY_MS * 2)
      return () => {
        clearTimeout(showOverlayTimer)
        clearTimeout(navigateTimer)
      }
    }
  }, [showConfirmation])

  useEffect(() => {
    if (_storyShowConfetti) {
      const timer = setTimeout(() => setStoryConfetti(true), 300)
      return () => clearTimeout(timer)
    }
  }, [_storyShowConfetti])

  const hasFeedbackBar = !!triageResult?.id && !isLive && !showActivityOnlyView
  const hasFloatingFooter = hasFeedbackBar

  return (
    <Dialog.Root open={true} modal={false}>
      <Dialog.Content
        ref={drawerContentRef}
        className={classNames(styles.content, footerStyles.drawerContentPaddingAdjustment)}
        data-has-floating-footer={hasFloatingFooter ? true : undefined}
        onEscapeKeyDown={onClose}
        onPointerDownOutside={event => event.preventDefault()}
        onInteractOutside={event => event.preventDefault()}
      >
        <VisuallyHidden>
          <Dialog.Title>Finding details</Dialog.Title>
          <Dialog.Description>View detailed information about a finding</Dialog.Description>
        </VisuallyHidden>

        <Dialog.Close asChild className={styles.closeButton}>
          <button aria-label="Close" onClick={onClose}>
            <XIcon className={styles.closeButtonIcon} size={24} weight="light" />
          </button>
        </Dialog.Close>
        <ErrorBoundary fallbackTitle="Unable to display triage details">
          {match({ isFindingLoading, isFindingError, isResultNotFound, finding })
            .with({ isFindingLoading: true }, () => (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  paddingTop: '64px',
                }}
              >
                <Spinner label="Loading finding" size="128" />
              </div>
            ))
            .with({ isFindingError: true }, () => (
              <div className={styles.noTriageText}>
                <p>Failed to load finding.</p>
                <p style={{ marginTop: '8px', fontSize: '14px' }}>Please try refreshing the page.</p>
              </div>
            ))
            .with({ isResultNotFound: true }, () => <ResultNotFoundFallback />)
            .with({ finding: P.not(P.nullish) }, ({ finding }) => {
              return (
                <>
                  <FindingDetails finding={finding} triageResult={triageResult} />
                  {match({ showActivityOnlyView })
                    .with({ showActivityOnlyView: true }, () => (
                      <TriageActivityOnlyView
                        activityProps={activityProps}
                        isLive={isLive ?? false}
                        showConfirmation={showConfirmation}
                        showFixTransitionOverlay={showFixTransitionOverlay}
                      />
                    ))
                    .otherwise(() => (
                      <TriageResultTabs
                        selectedTab={selectedTab}
                        onTabChange={handleTabChange}
                        finding={finding}
                        fixResult={fixResult}
                        isFixInProgress={isFixInProgress}
                        onOpenFindingFixDrawer={onOpenFindingFixDrawer}
                        completedTriageOutcome={completedTriageOutcome}
                        triageOutcome={triageOutcome}
                        summaryCopyText={summaryCopyText}
                        fullReportCopyText={fullReportCopyText}
                        triageArticleProps={triageArticleProps}
                        triageReportProps={triageReportProps}
                        triageVerificationProps={triageVerificationProps}
                        isActivityTabVisible={isActivityTabVisible}
                        isFullReportTabVisible={isFullReportTabVisible}
                        isVerificationTabVisible={isVerificationTabVisible}
                        isPreferencesTabVisible={isPreferencesTabVisible}
                        activityProps={activityProps}
                        memory={memory}
                        notification={notification}
                        notificationActionLabel={notificationActionLabel}
                        onNotificationAction={notificationAction}
                        onPush={onPush}
                        isPushInProgress={isPushInProgress}
                        pushIntegrationName={pushIntegrationName}
                        currentPushState={currentPushState}
                        isLive={isLive ?? false}
                        showConfirmation={showConfirmation}
                        showFixTransitionOverlay={showFixTransitionOverlay}
                      />
                    ))}
                </>
              )
            })
            .otherwise(() => null)}
          {(showConfetti || storyConfetti) && contentDimensions.width > 0 && (
            <DrawerConfetti
              width={contentDimensions.width}
              height={contentDimensions.height}
              onComplete={() => {
                onConfettiComplete()
                setStoryConfetti(false)
              }}
            />
          )}
        </ErrorBoundary>
      </Dialog.Content>
      {hasFeedbackBar && (
        <FeedbackBar
          resultId={triageResult.id}
          resultType="triage"
          scanId={scanId}
          drawerContentRef={drawerContentRef}
        />
      )}
    </Dialog.Root>
  )
}

// #endregion

// #region Sub-components

const FindingDetails: React.FC<{
  finding: FindingResponseWithoutEmbed
  triageResult?: ResultsTriageResultResponse
}> = ({ finding, triageResult }) => {
  return (
    <Dialog.Description className={styles.header} aria-label="Finding Details" asChild>
      <section>
        <Dialog.Title className={styles.title}>{finding.title}</Dialog.Title>
        <div className={styles.metadata}>
          <div className={styles.metadataItem}>
            <label id={`rule-label-${finding.id}`} className={styles.metadataItemLabel}>
              Rule
            </label>
            <span className={styles.metadataItemValue} aria-labelledby={`rule-label-${finding.id}`}>
              {finding.rule}
            </span>
          </div>
          <div className={styles.metadataItem}>
            <label id={`finding-label-${finding.id}`} className={styles.metadataItemLabel}>
              Finding
            </label>
            <span className={styles.metadataItemValue} aria-labelledby={`finding-label-${finding.id}`}>
              {finding.html_url ? (
                <a
                  href={finding.html_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.link}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                >
                  {finding.id}
                  <ArrowSquareOutIcon size={12} weight="light" style={{ color: mappedColors.neutral.halo }} />
                </a>
              ) : (
                finding.id
              )}
            </span>
          </div>
          <div className={styles.metadataItem}>
            <label id={`severity-label-${finding.id}`} className={styles.metadataItemLabel}>
              Severity
            </label>
            <span
              className={styles.metadataItemValue}
              aria-labelledby={`severity-label-${finding.id}`}
              style={{ textTransform: 'capitalize' }}
            >
              {finding.severity?.label}
            </span>
          </div>
          <div className={styles.metadataItem}>
            <label id={`result-label-${finding.id}`} className={styles.metadataItemLabel}>
              Result
            </label>
            <span
              className={styles.metadataItemValue}
              aria-labelledby={`result-label-${finding.id}`}
              style={getTriageResultLabel(triageResult).isItalic ? { fontStyle: 'italic' } : undefined}
            >
              {getTriageResultLabel(triageResult).text}
            </span>
          </div>
        </div>
      </section>
    </Dialog.Description>
  )
}

type TriageActivityOnlyViewProps = {
  activityProps: AnalysisActivityTabProps | undefined
  isLive: boolean
  showConfirmation: boolean
  showFixTransitionOverlay: boolean
}

const TriageActivityOnlyView: React.FC<TriageActivityOnlyViewProps> = ({
  activityProps,
  isLive,
  showConfirmation,
  showFixTransitionOverlay,
}) => (
  <div className={styles.tabContainer}>
    <div>
      {activityProps && (
        <AnalysisActivityTab
          {...activityProps}
          progressLabel="Triage analysis"
          isLive={isLive}
          overlay={showConfirmation ? <AnalysisTransitionOverlay message="Rendering triage result" /> : undefined}
        />
      )}
    </div>
    {showFixTransitionOverlay && (
      <AnalysisTransitionOverlay message="Opening fix analysis" ariaLabel="Fix transition" />
    )}
  </div>
)

type TriageResultTabsProps = {
  selectedTab: TriageFindingDrawerTab
  onTabChange: (value: string) => unknown
  finding: FindingResponseWithoutEmbed
  fixResult: FixResultResponse | undefined
  isFixInProgress: boolean
  onOpenFindingFixDrawer: (findingId: string | undefined, resultId?: string) => unknown
  completedTriageOutcome: CompletedTriageOutcome | undefined
  triageOutcome: OutcomeBannerOutcome | undefined
  summaryCopyText: string | undefined
  fullReportCopyText: string | undefined
  triageArticleProps: TriageArticleProps
  triageReportProps: TriageReportProps
  triageVerificationProps: TriageVerificationProps
  isActivityTabVisible: boolean
  isFullReportTabVisible: boolean
  isVerificationTabVisible: boolean
  isPreferencesTabVisible: boolean
  activityProps: AnalysisActivityTabProps | undefined
  memory: string | undefined
  notification: { status: StatusNotificationStatus; text: string } | undefined
  notificationActionLabel: string | undefined
  onNotificationAction: (() => unknown) | undefined
  onPush: (() => unknown) | undefined
  isPushInProgress: boolean | undefined
  pushIntegrationName: string | undefined
  currentPushState: PushResponse | undefined
  isLive: boolean
  showConfirmation: boolean
  showFixTransitionOverlay: boolean
}

const TriageResultTabs: React.FC<TriageResultTabsProps> = ({
  selectedTab,
  onTabChange,
  finding,
  fixResult,
  isFixInProgress,
  onOpenFindingFixDrawer,
  completedTriageOutcome,
  triageOutcome,
  summaryCopyText,
  fullReportCopyText,
  triageArticleProps,
  triageReportProps,
  triageVerificationProps,
  isActivityTabVisible,
  isFullReportTabVisible,
  isVerificationTabVisible,
  isPreferencesTabVisible,
  activityProps,
  memory,
  notification,
  notificationActionLabel,
  onNotificationAction,
  onPush,
  isPushInProgress,
  pushIntegrationName,
  currentPushState,
  isLive,
  showConfirmation,
  showFixTransitionOverlay,
}) => (
  <Tabs.Root value={selectedTab} onValueChange={onTabChange} className={styles.tabContainer}>
    <ScrollableTabBar
      className={styles.tabRow}
      trailingContent={match({
        isFixInProgress,
        fixResult,
        fixOutcomeType: fixResult?.outcome?.type,
        suggestedStatus: completedTriageOutcome?.suggested_status,
      })
        .with({ isFixInProgress: true, fixResult: P.not(P.nullish) }, () => (
          <div className={styles.tabRowRight}>
            <ProgressButton
              label="View fix analysis"
              onClick={() => onOpenFindingFixDrawer(finding.id, fixResult?.id)}
            />
          </div>
        ))
        .with({ fixOutcomeType: 'completed-fix' }, () => (
          <div className={styles.tabRowRight}>
            <GhostButton onClick={() => onOpenFindingFixDrawer(finding.id, fixResult?.id)}>
              <EyeIcon
                size={16}
                weight="fill"
                style={{
                  marginRight: '4px',
                  color: mappedColors.neutral.halo,
                }}
              />
              <span className={typography.small}>View fix result</span>
            </GhostButton>
          </div>
        ))
        .with({ fixOutcomeType: 'no-recommendations-fix' }, () => (
          <div className={styles.tabRowRight}>
            <GhostButton state="disabled">
              <span className={typography.small}>No fix necessary</span>
            </GhostButton>
          </div>
        ))
        .with({ suggestedStatus: 'false_positive' }, { suggestedStatus: 'wont_fix' }, () => (
          <div className={styles.tabRowRight}>
            <GhostButton state="disabled">
              <span className={typography.small}>No fix necessary</span>
            </GhostButton>
          </div>
        ))
        .otherwise(() => undefined)}
    >
      <Tabs.List className={scrollableTabBarStyles.tabList}>
        <Tabs.Trigger className={styles.tab} value={'summary' satisfies TriageFindingDrawerTab}>
          Summary
        </Tabs.Trigger>
        {isFullReportTabVisible && (
          <Tabs.Trigger className={styles.tab} value={'full-report' satisfies TriageFindingDrawerTab}>
            Full Report
          </Tabs.Trigger>
        )}
        {isVerificationTabVisible && (
          <Tabs.Trigger
            className={styles.tab}
            value={'verification' satisfies TriageFindingDrawerTab}
            aria-label="Verification"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              Verification
              <span className={styles.betaTag}>
                <BetaTag />
              </span>
            </div>
          </Tabs.Trigger>
        )}
        {isActivityTabVisible && (
          <Tabs.Trigger className={styles.tab} value={'analysis-activity' satisfies TriageFindingDrawerTab}>
            ANALYSIS ACTIVITY
          </Tabs.Trigger>
        )}
        {isPreferencesTabVisible && (
          <Tabs.Trigger className={styles.tab} value={'preferences' satisfies TriageFindingDrawerTab}>
            PREFERENCES
          </Tabs.Trigger>
        )}
      </Tabs.List>
    </ScrollableTabBar>

    {notification && (
      <div className={styles.notificationWrapper}>
        <StatusNotification
          status={notification.status}
          text={notification.text}
          actionLabel={notificationActionLabel}
          onAction={onNotificationAction}
        />
      </div>
    )}

    <Tabs.Content className={styles.tabContent} value="summary">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {triageOutcome && (
          <OutcomeBanner
            outcome={triageOutcome}
            copyText={summaryCopyText}
            copyButtonTooltip="Copy summary"
            onPush={onPush}
            isPushInProgress={isPushInProgress}
            pushIntegrationName={pushIntegrationName}
            pushState={currentPushState}
          />
        )}
        {completedTriageOutcome?.analyzer_metadata?.strategy && (
          <AnalyzerStrategyTag strategy={completedTriageOutcome.analyzer_metadata.strategy} />
        )}
        {isBlockedOrExcludedTriageOutcome(triageOutcome) ? (
          // Blocked/excluded findings have no triage article (the endpoint returns none), so show the
          // outcome's own reason/details instead of the "No triage article found" placeholder.
          <OutcomeReasonDetails
            reason={triageOutcome.reason}
            details={triageOutcome.details}
            fallback={
              <div className={styles.noTriageText}>
                This finding was {triageOutcome.type === 'excluded-triage' ? 'excluded from' : 'blocked from'} automatic
                triage.
              </div>
            }
          />
        ) : (
          <TriageArticle {...triageArticleProps} />
        )}
      </div>
    </Tabs.Content>
    <Tabs.Content className={styles.tabContent} value="full-report">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {triageOutcome && (
          <OutcomeBanner
            outcome={triageOutcome}
            copyText={fullReportCopyText}
            copyButtonTooltip="Copy full report"
            onPush={onPush}
            isPushInProgress={isPushInProgress}
            pushIntegrationName={pushIntegrationName}
            pushState={currentPushState}
          />
        )}
        {completedTriageOutcome?.analyzer_metadata?.strategy && (
          <AnalyzerStrategyTag strategy={completedTriageOutcome.analyzer_metadata.strategy} />
        )}
        <TriageReport {...triageReportProps} />
      </div>
    </Tabs.Content>
    <Tabs.Content className={styles.tabContent} value="verification">
      <ScanFindingTriageVerificationTab {...triageVerificationProps} />
    </Tabs.Content>
    {activityProps && (
      <Tabs.Content className={styles.tabContent} value={'analysis-activity' satisfies TriageFindingDrawerTab}>
        <AnalysisActivityTab
          {...activityProps}
          progressLabel="Triage analysis"
          isLive={isLive}
          overlay={showConfirmation ? <AnalysisTransitionOverlay message="Rendering triage result" /> : undefined}
        />
      </Tabs.Content>
    )}
    {memory && (
      <Tabs.Content className={styles.tabContent} value={'preferences' satisfies TriageFindingDrawerTab}>
        <AnalysisPreferencesTab memory={memory} />
      </Tabs.Content>
    )}
    {showFixTransitionOverlay && (
      <AnalysisTransitionOverlay message="Opening fix analysis" ariaLabel="Fix transition" />
    )}
  </Tabs.Root>
)

const TriageArticle: React.FC<TriageArticleProps> = ({ data, isError, isLoading }) => {
  const formattedMarkdown = useMemo(() => {
    if (!data || data === '') return undefined
    return reformatFindingTriageArticleMarkdown(data)
  }, [data])

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          paddingTop: '64px',
        }}
      >
        <Spinner label="Loading finding triage article" size="128" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className={styles.noTriageText}>
        <p>Failed to load triage article.</p>
        <p style={{ marginTop: '8px', fontSize: '14px' }}>Please try refreshing the page.</p>
      </div>
    )
  }

  if (!formattedMarkdown) {
    return <div className={styles.noTriageText}>No triage article found for this finding.</div>
  }

  return <RenderedMarkdown markdown={formattedMarkdown} />
}

const TriageReport: React.FC<TriageReportProps> = ({ data, isError, isLoading }) => {
  const formattedMarkdown = useMemo(() => reformatFindingTriageReportMarkdown(data), [data])

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          paddingTop: '64px',
        }}
      >
        <Spinner label="Loading finding triage report" size="128" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className={styles.noTriageText}>
        <p>Failed to load triage report.</p>
        <p style={{ marginTop: '8px', fontSize: '14px' }}>Please try refreshing the page.</p>
      </div>
    )
  }

  if (!formattedMarkdown) {
    return <div className={styles.noTriageText}>No triage report found for this finding.</div>
  }

  return <RenderedMarkdown markdown={formattedMarkdown} />
}

const ScanFindingTriageVerificationTab: React.FC<TriageVerificationProps> = ({ data, isError }) => {
  if (isError) {
    return (
      <div className={styles.noTriageText}>
        <p>Failed to load finding verification.</p>
        <p style={{ marginTop: '8px', fontSize: '14px' }}>Please try refreshing the page.</p>
      </div>
    )
  }

  return <VerificationTab verification={data ?? undefined} />
}

export const getTriageResultLabel = (
  triageResult?: ResultsTriageResultResponse
): { text: string; isItalic?: boolean } => {
  if (!triageResult) {
    return { text: 'Not Triaged' }
  }

  if (!triageResult.outcome) {
    return { text: 'Processing', isItalic: true }
  }

  return (
    match(triageResult.outcome)
      .with({ type: 'completed-triage' }, () => ({ text: 'Triaged' }))
      .with({ type: 'failed-triage' }, outcome => {
        if (outcome.problem.code === 'rule_not_supported') {
          return { text: 'Rule Not Supported' }
        }
        if (outcome.problem.code === 'error') {
          return { text: 'Error' }
        }
        return { text: 'Unknown' }
      })
      .with({ type: 'no-recommendations-triage' }, () => ({ text: 'No Recommendations' }))
      .with({ type: 'blocked-triage' }, () => ({ text: 'Not Triaged' }))
      .with({ type: 'excluded-triage' }, () => ({ text: 'Not Triaged' }))
      // Cancelled triage: audit-only. Treat as unanalyzed — same "Processing" text used
      // above for an outcome-less result, since the finding will surface as unanalyzed.
      .with({ type: 'cancelled-triage' }, () => ({ text: 'Processing', isItalic: true }))
      .exhaustive()
  )
}

type Verification = ResultsVerificationResponse

const VerificationTab: React.FC<{ verification?: Verification }> = ({ verification }) => {
  const verificationCopyText = useMemo(
    () => (verification ? getVerificationResultsToCopyText(verification) : undefined),
    [verification]
  )

  if (!verification) {
    return null
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <VerificationOutcomeBanner
        result={verification.result}
        copyText={verificationCopyText}
        copyButtonTooltip="Copy verification"
      />
      {verification.headlines.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {verification.headlines.map(headline => (
            <VerificationHeadline key={headline.title} headline={headline} />
          ))}
        </div>
      )}
      <RenderedMarkdown markdown={verification.body} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {verification.code_blocks.map(codeBlock => (
          <CollapsibleCodeBlock key={codeBlock.filename} codeBlock={codeBlock} />
        ))}
        <CollapsibleResultsTable payload_results={verification.payload_results} />
      </div>
    </div>
  )
}

const VerificationHeadline: React.FC<{ headline: Verification['headlines'][number] }> = ({ headline }) => (
  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
    <span className={typography.column} style={{ color: mappedColors.neutral.neutral30 }}>
      {headline.title}
    </span>

    <span className={typography.code} style={{ color: mappedColors.neutral.neutral10 }}>
      {headline.description}
    </span>
  </div>
)

const CollapsibleCodeBlock: React.FC<{
  codeBlock: Verification['code_blocks'][number]
}> = ({ codeBlock }) => {
  return (
    <Collapsible.Root key={codeBlock.filename}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Collapsible.Trigger className={styles.codeBlockTrigger}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <span style={{ color: mappedColors.neutral.neutral10 }}>{codeBlock.title}</span>
              <CaretRightIcon className={styles.caretIcon} size={24} weight="regular" />
            </div>
          </Collapsible.Trigger>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <span className={typography.smallBold} style={{ color: mappedColors.neutral.neutral10 }}>
              {codeBlock.filename}
            </span>
            <TooltipWithHover
              trigger={
                <button
                  aria-label={`Download ${codeBlock.filename}`}
                  style={{ lineHeight: '0' }}
                  onClick={() => downloadFile(codeBlock.content, codeBlock.filename)}
                >
                  <DownloadSimpleIcon size={20} weight="light" style={{ color: mappedColors.neutral.halo }} />
                </button>
              }
              side="left"
            >
              Download file
            </TooltipWithHover>
          </div>
        </div>
        <Collapsible.Content aria-label={`${codeBlock.filename} content`} className={styles.collapsibleContent}>
          <div style={{ padding: '16px', display: 'flex', gap: '8px' }} className={styles.codeBlockContent}>
            <RenderedMarkdown
              markdown={wrapInCodeFence(codeBlock.content, inferLanguageFromFilename(codeBlock.filename))}
            />
            <CopyButton copyText={codeBlock.content} label={`Copy ${codeBlock.filename} content`} size="medium" />
          </div>
        </Collapsible.Content>
      </div>
    </Collapsible.Root>
  )
}

const CollapsibleResultsTable: React.FC<{
  payload_results: Verification['payload_results']
}> = ({ payload_results }) => {
  return (
    <Collapsible.Root>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Collapsible.Trigger className={styles.codeBlockTrigger}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <span style={{ color: mappedColors.neutral.neutral10 }}>Results</span>
              <CaretRightIcon className={styles.caretIcon} size={24} weight="regular" />
            </div>
          </Collapsible.Trigger>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <span className={typography.smallBold} style={{ color: mappedColors.neutral.neutral10 }}>
              all_results.json
            </span>
            <TooltipWithHover
              trigger={
                <button
                  aria-label={`Download all_results.json`}
                  style={{ lineHeight: '0' }}
                  onClick={() => downloadFile(JSON.stringify(payload_results), 'all_results.json')}
                >
                  <DownloadSimpleIcon size={20} weight="light" style={{ color: mappedColors.neutral.halo }} />
                </button>
              }
              side="left"
            >
              Download file
            </TooltipWithHover>
          </div>
        </div>
        <Collapsible.Content aria-label={`all_results.json content`} className={styles.collapsibleContent}>
          <ResultsTable payload_results={payload_results} />
        </Collapsible.Content>
      </div>
    </Collapsible.Root>
  )
}

const ResultsTable: React.FC<{ payload_results: Verification['payload_results'] }> = ({ payload_results }) => {
  if (!payload_results) return null

  const rows = payload_results.rows.map((row, i) => ({
    id: String(i),
    ...row,
  }))

  const columns: ColumnDef<RowDataWithId<Record<string, React.ReactNode>>>[] = payload_results.columns.map(column => ({
    header: () => (
      <span
        className={typography.codeBold}
        style={{ color: mappedColors.neutral.neutral10, textTransform: 'uppercase' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', width: '100%' }}>{column}</div>
      </span>
    ),
    cell: ({ row }) => (
      <span
        className={typography.code}
        style={{ color: mappedColors.neutral.neutral10, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', width: '100%' }}>
          {row.original[column]}
        </div>
      </span>
    ),
    accessorKey: column,
  }))

  return (
    <div style={{ overflowX: 'auto' }}>
      <Table data={rows} columns={columns} getRowId={row => row.id} />
    </div>
  )
}

// Memoized because reformatting and concatenating the article/report markdown is
// expensive enough to avoid on every render of the drawer.
const useTriageTabCopyTexts = ({
  triageOutcome,
  articleMarkdown,
  reportMarkdown,
}: {
  triageOutcome?: OutcomeBannerOutcome
  articleMarkdown?: string
  reportMarkdown?: string
}) => {
  const summaryCopyText = useMemo(
    () =>
      triageOutcome
        ? joinCopySections(
            getOutcomeBannerCopyText(triageOutcome),
            // Blocked/excluded findings have no article — the Summary tab shows their reason/details,
            // so the copy button must include that same text rather than an empty article section.
            isBlockedOrExcludedTriageOutcome(triageOutcome)
              ? joinCopySections(triageOutcome.reason, triageOutcome.details)
              : reformatFindingTriageArticleMarkdown(articleMarkdown)
          )
        : undefined,
    [triageOutcome, articleMarkdown]
  )

  const fullReportCopyText = useMemo(
    () =>
      triageOutcome
        ? joinCopySections(getOutcomeBannerCopyText(triageOutcome), reformatFindingTriageReportMarkdown(reportMarkdown))
        : undefined,
    [triageOutcome, reportMarkdown]
  )

  return { summaryCopyText, fullReportCopyText }
}

const convertVerificationCodeBlockToMarkdown = (codeBlock: Verification['code_blocks'][number]) =>
  `${codeBlock.title} — \`${codeBlock.filename}\`

${wrapInCodeFence(codeBlock.content, inferLanguageFromFilename(codeBlock.filename))}`

const convertVerificationResultsTableToMarkdown = (payloadResults: Verification['payload_results']) => {
  if (!payloadResults || payloadResults.columns.length === 0) {
    return undefined
  }

  const { columns, rows } = payloadResults
  const escapeCell = (value: string) => value.replace(/\|/g, '\\|').replace(/\n/g, '<br>')
  const toRow = (cells: string[]) => `| ${cells.join(' | ')} |`

  return [
    'Results (all_results.json)',
    '',
    toRow(columns),
    toRow(columns.map(() => '---')),
    ...rows.map(row => toRow(columns.map(column => escapeCell(row[column] ?? '')))),
  ].join('\n')
}

const getVerificationResultsToCopyText = (verification: Verification) =>
  joinCopySections(
    `Simulated result\n${convertVerificationResultToLabel(verification.result)}`,
    verification.headlines.map(headline => `${headline.title}\n${headline.description}`).join('\n\n') || undefined,
    verification.body,
    verification.code_blocks.map(convertVerificationCodeBlockToMarkdown).join('\n\n') || undefined,
    convertVerificationResultsTableToMarkdown(verification.payload_results)
  )

const downloadFile = (content: string, filename: string, contentType = 'text/plain') => {
  const blob = new Blob([content], { type: contentType })

  const url = URL.createObjectURL(blob)

  const a = document.createElement('a')
  a.href = url
  a.download = filename

  document.body.appendChild(a)
  a.click()

  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// #endregion
